import { useGateValue } from '@statsig/expo-bindings'
import { isSameMonth, startOfDay } from 'date-fns'
import * as Device from 'expo-device'
import * as Notifications from 'expo-notifications'
import { useNavigation } from 'expo-router'
import { memo, useCallback, useEffect, useState } from 'react'
import {
  ActivityIndicator,
  FlatList,
  Platform,
  useWindowDimensions,
  View,
} from 'react-native'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import DashboardEventCard from '@/components/dashboard/DashboardEventCard'
import DashboardTimeline from '@/components/dashboard/DashboardTimeline'
import { DashboardDay } from '@/components/dashboard/DashboardTypes'
import BillingMessage from '@/components/shared/BillingMessage'
import PushNotificationModal from '@/components/shared/PushNotificationModal'
import WelcomeModal from '@/components/shared/WelcomeModal'
import TourSpot from '@/components/tour/TourSpot'
import UniDivider from '@/components/unistyles/UniDivider'
import UniIcon from '@/components/unistyles/UniIcon'
import UniKeyboardAwareScrollView from '@/components/unistyles/UniKeyboardAwareScrollView'
import UniRefreshControl from '@/components/unistyles/UniRefreshControl'
import UniSegmentedButtons from '@/components/unistyles/UniSegmentedButtons'
import UniText from '@/components/unistyles/UniText'
import useAppTour from '@/hooks/useAppTour'
import useRefetchOnFocus from '@/hooks/useRefetchOnFocus'
import useUserRoles from '@/hooks/useUserRoles'
import { useAppStore } from '@/store/useAppStore'
import trpc from '@/utils/trpc'
import { AppTourKeys, Feature } from '../../../constants'

const DayItem = memo(
  ({
    day,
    dayIndex,
    isCurrentMonth,
    showUserInfo,
    eventsByDayAndFoster,
  }: {
    day: DashboardDay
    dayIndex: number
    isCurrentMonth: (date: Date) => boolean
    showUserInfo: boolean
    eventsByDayAndFoster: DashboardDay[]
  }) => {
    const { theme } = useUnistyles()
    const { fontScale } = useWindowDimensions()

    return (
      <View style={styles.dayContainer}>
        {dayIndex > 0 &&
          !isSameMonth(
            day.dueDate,
            eventsByDayAndFoster[dayIndex - 1].dueDate
          ) && (
            <View style={styles.rowContainer}>
              <UniIcon
                color={theme.colors.secondary}
                size={20}
                source="calendar-month"
              />
              <View style={styles.monthLabelContainer}>
                <UniText style={styles.monthLabel}>
                  {day.dueDate.toLocaleDateString('en-US', {
                    month: 'long',
                  })}
                </UniText>
                {isCurrentMonth(day.dueDate) && (
                  <UniText style={styles.currentMonthLabel}>(Current)</UniText>
                )}
              </View>
              <UniDivider style={styles.monthDivider} />
            </View>
          )}
        <View style={styles.eventsContainer}>
          {fontScale <= 2 && <DashboardTimeline day={day} />}
          <View style={styles.cardsContainer}>
            {dayIndex === 0 && !day.fosters.length && (
              <View style={styles.zeroStateToday}>
                <UniText style={styles.zeroStateTodayText}>
                  All caught up for today! 🎉
                </UniText>
                <UniText style={styles.zeroStateSubText}>
                  Check back later for upcoming tasks
                </UniText>
              </View>
            )}
            {day.fosters.map((foster) => (
              <DashboardEventCard
                foster={foster}
                isOverdue={day.isOverdue}
                key={`${foster.fosterId}-${startOfDay(day.dueDate).getTime()}`}
                showUserInfo={showUserInfo}
              />
            ))}
          </View>
        </View>
      </View>
    )
  }
)

DayItem.displayName = 'DayItem'

const DashboardTab = () => {
  const { theme } = useUnistyles()
  const [includeEveryone, setIncludeEveryone] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const { isUserAdmin } = useUserRoles()

  Notifications.setBadgeCountAsync(0)

  const navigation = useNavigation()

  const currentOrganization = useAppStore.use.currentOrganization().organization

  const currentUserQuery = trpc.user.current.useQuery(
    {
      organizationId: currentOrganization?.id,
    },
    {
      enabled: !!currentOrganization,
    }
  )

  const events = trpc.dashboard.events.useQuery(
    {
      organizationId: currentOrganization?.id || '',
      includeEveryone,
    },
    {
      enabled: !!currentOrganization,
    }
  )

  useRefetchOnFocus(events.refetch)

  const eventsByDayAndFoster = events.data

  const [showPushModal, setShowPushModal] = useState(false)

  const hasSeenWelcomeModal = useAppStore.use.hasSeenWelcomeModal()
  const setHasSeenWelcomeModal = useAppStore.use.setHasSeenWelcomeModal()
  const hasSeenPushNotificationModal =
    useAppStore.use.hasSeenPushNotificationModal()
  const setHasSeenPushNotificationModal =
    useAppStore.use.setHasSeenPushNotificationModal()
  const setAppTourStepComplete = useAppStore.use.setAppTourStepComplete()
  const hasFirstRunExperience = useGateValue(Feature.FirstRunExperience)
  const { fontScale } = useWindowDimensions()

  useAppTour({
    enabled: hasSeenWelcomeModal && hasSeenPushNotificationModal,
    tourKey: AppTourKeys.DashboardGrid,
  })

  useEffect(() => {
    if (!currentOrganization) {
      return
    }

    navigation.setOptions({
      title: currentOrganization.name,
    })
  }, [navigation, currentOrganization])

  useEffect(() => {
    if (
      hasSeenWelcomeModal &&
      !hasSeenPushNotificationModal &&
      hasFirstRunExperience
    ) {
      setShowPushModal(true)
    }
  }, [hasSeenWelcomeModal, hasSeenPushNotificationModal, hasFirstRunExperience])

  const onRefresh = useCallback(async () => {
    setRefreshing(true)
    await events.refetch()
    setRefreshing(false)
  }, [events])

  const isCurrentMonth = useCallback(
    (date: Date) => isSameMonth(date, new Date()),
    []
  )

  const keyExtractor = useCallback(
    (item: DashboardDay, index: number) =>
      `${item.isOverdue}-${startOfDay(item.dueDate).getTime()}-${index}`,
    []
  )

  const renderItem = useCallback(
    ({ item: day, index }: { item: DashboardDay; index: number }) => (
      <DayItem
        day={day}
        dayIndex={index}
        eventsByDayAndFoster={eventsByDayAndFoster || []}
        isCurrentMonth={isCurrentMonth}
        showUserInfo={includeEveryone}
      />
    ),
    [isCurrentMonth, includeEveryone, eventsByDayAndFoster]
  )

  const renderScrollComponent = useCallback(
    (props: any) => (
      <UniKeyboardAwareScrollView
        {...props}
        contentInsetAdjustmentBehavior="automatic"
        refreshControl={
          <UniRefreshControl onRefresh={onRefresh} refreshing={refreshing} />
        }
      />
    ),
    [refreshing, onRefresh]
  )

  const handleStartTour = () => {
    setHasSeenWelcomeModal(true)
  }

  const handleSkipTour = () => {
    setHasSeenWelcomeModal(true)
    Object.values(AppTourKeys).forEach((key) => {
      setAppTourStepComplete(key, true)
    })
  }

  const handleAllowPush = async () => {
    setShowPushModal(false)
    setHasSeenPushNotificationModal(true)
    if (Device.isDevice) {
      const { status: existingStatus } =
        await Notifications.getPermissionsAsync()
      if (existingStatus !== 'granted') {
        await Notifications.requestPermissionsAsync()
      }
    }
  }

  const handleSkipPush = () => {
    setShowPushModal(false)
    setHasSeenPushNotificationModal(true)
  }

  return (
    <View style={styles.container}>
      <WelcomeModal
        onSkip={handleSkipTour}
        onStartTour={handleStartTour}
        visible={
          Platform.OS !== 'web' && !hasSeenWelcomeModal && hasFirstRunExperience
        }
      />
      <PushNotificationModal
        onAllow={handleAllowPush}
        onSkip={handleSkipPush}
        visible={Platform.OS !== 'web' && showPushModal}
      />
      {events.isLoading || currentUserQuery.isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator color={theme.colors.primary} size="large" />
        </View>
      ) : (
        <>
          <BillingMessage requiresSubscription={true} />
          <TourSpot id="dashboard-tab" style={styles.flexFill}>
            <View style={styles.flexFill}>
              {isUserAdmin && (
                <View style={styles.segmentedButtonContainer}>
                  <TourSpot
                    id="dashboard-admin-buttons"
                    style={styles.tourGuideZone}
                  >
                    <UniSegmentedButtons
                      buttons={[
                        {
                          icon: 'account',
                          value: 'justme',
                          label: 'My Fosters',
                        },
                        {
                          icon: 'account-group',
                          value: 'everyone',
                          label: 'All Fosters',
                        },
                      ]}
                      onValueChange={(value: string) => {
                        setIncludeEveryone(value === 'everyone')
                      }}
                      style={[
                        styles.segmentedButtons,
                        { maxWidth: 250 * fontScale },
                      ]}
                      value={includeEveryone ? 'everyone' : 'justme'}
                    />
                  </TourSpot>
                </View>
              )}
              <FlatList
                data={eventsByDayAndFoster}
                initialNumToRender={5}
                keyExtractor={keyExtractor}
                maxToRenderPerBatch={5}
                renderItem={renderItem}
                renderScrollComponent={renderScrollComponent}
                style={styles.list}
                windowSize={4}
              />
            </View>
          </TourSpot>
        </>
      )}
    </View>
  )
}

DashboardTab.displayName = 'DashboardTab'

export default DashboardTab

const styles = StyleSheet.create((theme) => {
  return {
    cardsContainer: {
      flex: 1,
      gap: theme.tokens.spacing[3],
      paddingRight: theme.tokens.spacing[2],
    },
    container: {
      flex: 1,
      width: '100%',
    },
    currentMonthLabel: {
      color: theme.colors.primary,
      fontSize: 12,
      fontWeight: '500',
      marginLeft: theme.tokens.spacing[2],
    },
    dayContainer: {
      marginBottom: theme.tokens.spacing[4],
      width: '100%',
    },
    flexFill: {
      flex: 1,
    },
    eventsContainer: {
      flexDirection: 'row',
      flex: 1,
      paddingHorizontal: theme.tokens.spacing[2],
    },
    list: {
      flex: 1,
      marginTop: theme.tokens.spacing[4],
      paddingLeft: theme.tokens.spacing[2],
      paddingRight: theme.tokens.spacing[2],
      width: '100%',
    },
    loadingContainer: {
      alignItems: 'center',
      flex: 1,
      justifyContent: 'center',
    },
    monthDivider: {
      backgroundColor: theme.colors.secondary,
      flex: 1,
      height: 1,
      opacity: 0.3,
    },
    monthLabel: {
      color: theme.colors.secondary,
      fontSize: 16,
      fontWeight: Platform.select({
        ios: '600',
        android: '700',
      }),
      textTransform: 'uppercase',
    },
    monthLabelContainer: {
      alignItems: 'center',
      flexDirection: 'row',
    },
    rowContainer: {
      alignItems: 'center',
      flexDirection: 'row',
      flex: 1,
      gap: theme.tokens.spacing[4],
      justifyContent: 'flex-start',
      marginBottom: theme.tokens.spacing[4],
      marginTop: theme.tokens.spacing[4],
      paddingHorizontal: theme.tokens.spacing[4],
    },
    segmentedButtonContainer: {
      marginBottom: theme.tokens.spacing[1],
      marginLeft: theme.tokens.spacing[2],
      marginRight: theme.tokens.spacing[2],
      marginTop: theme.tokens.spacing[3],
    },
    segmentedButtons: {
      alignSelf: 'flex-end',
      backgroundColor: theme.colors.surface,
      borderRadius: 20,
      marginBottom: theme.tokens.spacing[2],
      marginLeft: theme.tokens.spacing[2],
      marginRight: theme.tokens.spacing[2],
      marginTop: theme.tokens.spacing[2],
    },
    tourGuideZone: {
      alignSelf: 'flex-end',
      marginRight: theme.tokens.spacing[1],
    },
    zeroStateSubText: {
      color: theme.colors.onSurfaceVariant,
      fontSize: 14,
      opacity: 0.8,
    },
    zeroStateToday: {
      alignItems: 'center',
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.cardBorder,
      borderRadius: theme.tokens.containerBorderRadius,
      borderWidth: 1,
      flexDirection: 'column',
      gap: theme.tokens.spacing[2],
      justifyContent: 'center',
      marginLeft: theme.tokens.spacing[3],
      marginRight: theme.tokens.spacing[1],
      padding: theme.tokens.spacing[4],
    },
    zeroStateTodayText: {
      color: theme.colors.onSurfaceVariant,
      fontSize: 16,
      fontWeight: '500',
    },
  }
})
